/**
 * Badge definitions (spec §2.9). Badges live in the `badges` table as
 * `rule_json` so new ones can be added without a client release; this file
 * is the seed list and the rule grammar the evaluator understands.
 */

export type BadgeCategory = 'firsts' | 'range' | 'volume' | 'social' | 'meta';

export type StatKey =
  | 'pixels_total'
  | 'overwrites_total'
  | 'restores_total'
  | 'collab_events'
  | 'crowd_events'
  | 'playback_uses'
  | 'globe_cells'
  | 'cells_distinct'
  | 'tiles_distinct'
  | 'countries_distinct';

export type BadgeEvent =
  | 'first_pixel'
  | 'avatar_completed'
  | 'first_in_cell'
  | 'first_in_tile'
  | 'ink_cap'
  | 'ink_empty';

export type BadgeRule =
  | { type: 'stat_gte'; stat: StatKey; value: number }
  | { type: 'event'; event: BadgeEvent }
  /** Painted at a local hour in [from, to). */
  | { type: 'local_hour'; from: number; to: number }
  /** Painted on both the Saturday and Sunday of one weekend (local time). */
  | { type: 'weekend_both' }
  /** A pixel of yours survived unpainted-over for N days. */
  | { type: 'pixel_survived_days'; days: number }
  /** Account created within founder_window_days of launch. */
  | { type: 'founder' };

export interface BadgeDefinition {
  key: string;
  name: string;
  description: string;
  category: BadgeCategory;
  rule: BadgeRule;
}

export const BADGE_DEFINITIONS: BadgeDefinition[] = [
  // Firsts
  { key: 'first_mark', name: 'First Mark', description: 'Painted your first pixel.', category: 'firsts', rule: { type: 'event', event: 'first_pixel' } },
  { key: 'self_portrait', name: 'Self Portrait', description: 'Completed your avatar.', category: 'firsts', rule: { type: 'event', event: 'avatar_completed' } },
  { key: 'night_owl', name: 'Night Owl', description: 'Painted between midnight and 4 am.', category: 'firsts', rule: { type: 'local_hour', from: 0, to: 4 } },
  { key: 'early_bird', name: 'Early Bird', description: 'Painted between 5 and 7 am.', category: 'firsts', rule: { type: 'local_hour', from: 5, to: 7 } },
  { key: 'weekend_warrior', name: 'Weekend Warrior', description: 'Painted on both days of a weekend.', category: 'firsts', rule: { type: 'weekend_both' } },

  // Range
  { key: 'wanderer', name: 'Wanderer', description: 'Painted in 5 different areas.', category: 'range', rule: { type: 'stat_gte', stat: 'cells_distinct', value: 5 } },
  { key: 'explorer', name: 'Explorer', description: 'Painted in 25 different areas.', category: 'range', rule: { type: 'stat_gte', stat: 'cells_distinct', value: 25 } },
  { key: 'nomad', name: 'Nomad', description: 'Painted in 100 different areas.', category: 'range', rule: { type: 'stat_gte', stat: 'cells_distinct', value: 100 } },
  { key: 'passport_2', name: 'Passport', description: 'Painted in 2 countries.', category: 'range', rule: { type: 'stat_gte', stat: 'countries_distinct', value: 2 } },
  { key: 'passport_5', name: 'Passport II', description: 'Painted in 5 countries.', category: 'range', rule: { type: 'stat_gte', stat: 'countries_distinct', value: 5 } },
  { key: 'passport_10', name: 'Passport III', description: 'Painted in 10 countries.', category: 'range', rule: { type: 'stat_gte', stat: 'countries_distinct', value: 10 } },
  { key: 'pioneer', name: 'Pioneer', description: 'First person to paint in an area.', category: 'range', rule: { type: 'event', event: 'first_in_cell' } },
  { key: 'homesteader', name: 'Homesteader', description: 'First person to paint in a tile.', category: 'range', rule: { type: 'event', event: 'first_in_tile' } },

  // Volume
  { key: 'dabbler', name: 'Dabbler', description: '100 lifetime pixels.', category: 'volume', rule: { type: 'stat_gte', stat: 'pixels_total', value: 100 } },
  { key: 'painter', name: 'Painter', description: '1,000 lifetime pixels.', category: 'volume', rule: { type: 'stat_gte', stat: 'pixels_total', value: 1000 } },
  { key: 'muralist', name: 'Muralist', description: '10,000 lifetime pixels.', category: 'volume', rule: { type: 'stat_gte', stat: 'pixels_total', value: 10000 } },
  { key: 'monumental', name: 'Monumental', description: '100,000 lifetime pixels.', category: 'volume', rule: { type: 'stat_gte', stat: 'pixels_total', value: 100000 } },
  { key: 'full_tank', name: 'Full Tank', description: 'Reached the ink cap.', category: 'volume', rule: { type: 'event', event: 'ink_cap' } },
  { key: 'empty_tank', name: 'Empty Tank', description: 'Spent all your ink in one session.', category: 'volume', rule: { type: 'event', event: 'ink_empty' } },

  // Social
  { key: 'coverup', name: 'Coverup', description: 'Painted over 100 pixels by others.', category: 'social', rule: { type: 'stat_gte', stat: 'overwrites_total', value: 100 } },
  { key: 'restorer', name: 'Restorer', description: 'Restored 100 pixels to a colour they held a day earlier.', category: 'social', rule: { type: 'stat_gte', stat: 'restores_total', value: 100 } },
  { key: 'neighbour', name: 'Neighbour', description: 'Someone painted alongside you 10 times.', category: 'social', rule: { type: 'stat_gte', stat: 'collab_events', value: 10 } },
  { key: 'crowd', name: 'Crowd', description: 'Painted in a tile while 5 others were active.', category: 'social', rule: { type: 'stat_gte', stat: 'crowd_events', value: 1 } },
  { key: 'persistent_30', name: 'Persistent', description: 'A pixel of yours survived 30 days.', category: 'social', rule: { type: 'pixel_survived_days', days: 30 } },
  { key: 'persistent_90', name: 'Persistent II', description: 'A pixel of yours survived 90 days.', category: 'social', rule: { type: 'pixel_survived_days', days: 90 } },
  { key: 'persistent_365', name: 'Persistent III', description: 'A pixel of yours survived a year.', category: 'social', rule: { type: 'pixel_survived_days', days: 365 } },

  // Meta
  { key: 'time_traveller', name: 'Time Traveller', description: 'Used playback 10 times.', category: 'meta', rule: { type: 'stat_gte', stat: 'playback_uses', value: 10 } },
  { key: 'globetrotter', name: 'Globetrotter', description: 'Viewed 50 areas on the globe.', category: 'meta', rule: { type: 'stat_gte', stat: 'globe_cells', value: 50 } },
  { key: 'founder', name: 'Founder', description: 'Joined in the first days of Draw Earth.', category: 'meta', rule: { type: 'founder' } },
];

export const PERSISTENCE_THRESHOLDS_DAYS: number[] = BADGE_DEFINITIONS.flatMap((b) =>
  b.rule.type === 'pixel_survived_days' ? [b.rule.days] : [],
).sort((a, b) => a - b);

export function isBadgeRule(v: unknown): v is BadgeRule {
  if (!v || typeof v !== 'object') return false;
  const r = v as Record<string, unknown>;
  switch (r.type) {
    case 'stat_gte':
      return typeof r.stat === 'string' && typeof r.value === 'number';
    case 'event':
      return typeof r.event === 'string';
    case 'local_hour':
      return typeof r.from === 'number' && typeof r.to === 'number';
    case 'weekend_both':
    case 'founder':
      return true;
    case 'pixel_survived_days':
      return typeof r.days === 'number';
    default:
      return false;
  }
}
